import type {
  CancelOrderResponse,
  ExitMessage,
  MarginMode,
  PlaceOrderResponse,
  SetLeverageResponse,
  SetTradingStopResponse,
  Side,
  SymbolName,
  TimeInForce,
  TpSl,
  TpSlMode,
  TriggerBy,
} from "@ekidenfi/ts-sdk";
import { translateOrderError } from "@ekidenfi/ts-sdk";
import type { BN } from "@utils/BN";
import { extractApiErrorMessage } from "@utils/extractApiErrorMessage";

import { getEkidenSdkClient } from "@src/api/sdk";

import type { SubAccount } from "../account/types";

export interface TpSlData {
  takeProfitPrice: BN;
  stopLossPrice: BN;
}

export interface OpenOrderParams {
  tradingAccount: SubAccount;
  symbol: SymbolName;
  size: BN;
  price: BN;
  side: "buy" | "sell";
  type: "limit" | "market";
  timeInForce?: "GTC" | "IOC" | "FOK" | "PostOnly" | "GTD";
  gtdExpireAtMs?: number;
  reduceOnly?: boolean;
  tpSlData?: TpSlData;
}

export interface CloseOrdersParams {
  subAccountAddress: string;
  symbol: SymbolName;
  orderIds: string[];
}

export interface ClosePositionParams {
  subAccountAddress: string;
  symbol: SymbolName;
  size: BN;
  price: BN;
  side: "buy" | "sell";
  type: "limit" | "market";
}

export interface ClosePositionsParams {
  positions: ClosePositionParams[];
}

export interface SetLeverageParams {
  subAccountAddress: string;
  symbol: SymbolName;
  leverage: number;
}

export interface SetTradingStopParams {
  subAccountAddress: string;
  symbol: SymbolName;
  tpSlData: TpSlData;
  side: "buy" | "sell";
  entryPrice: BN;
}

export interface TradeResult {
  success: boolean;
  response?: PlaceOrderResponse | CancelOrderResponse;
  error?: string;
}

export class TradeService {
  openOrder = async (params: OpenOrderParams): Promise<TradeResult> => {
    const {
      tradingAccount,
      symbol,
      size,
      price,
      side,
      type,
      timeInForce = "GTC",
      gtdExpireAtMs,
      reduceOnly = false,
      tpSlData,
    } = params;

    try {
      const tpsl = this.buildTpSl(tpSlData, side, price);
      const sdkSide: Side = side === "buy" ? "Buy" : "Sell";
      const sdkTimeInForce = this.mapTimeInForce(timeInForce, gtdExpireAtMs);
      const expireTimeMs =
        timeInForce === "GTD" && gtdExpireAtMs != null && Number.isFinite(gtdExpireAtMs)
          ? Math.trunc(gtdExpireAtMs)
          : undefined;

      const response = await getEkidenSdkClient().trade.placeOrder({
        sub_account_address: tradingAccount.address,
        symbol,
        side: sdkSide,
        order_type: type === "market" ? "Market" : "Limit",
        qty: size.toString(),
        price: type === "market" ? "0" : price.toString(),
        margin_mode: "Cross" as MarginMode,
        time_in_force: sdkTimeInForce,
        post_only: timeInForce === "PostOnly",
        reduce_only: reduceOnly,
        close_on_trigger: false,
        // Backend expects i64 millis; SDK typings use string — serde rejects JSON strings here.
        ...(expireTimeMs != null ? { expire_time: expireTimeMs as unknown as string } : {}),
        tpsl,
      });

      return { success: true, response };
    } catch (error) {
      const rawMessage = extractApiErrorMessage(error);
      const message = translateOrderError(rawMessage) ?? rawMessage ?? "Failed to place order";
      return { success: false, error: message };
    }
  };

  closeOrders = async (params: CloseOrdersParams): Promise<TradeResult> => {
    const { subAccountAddress, symbol, orderIds } = params;

    try {
      if (orderIds.length === 1) {
        const response = await getEkidenSdkClient().trade.cancelOrder({
          sub_account_address: subAccountAddress,
          symbol,
          order_id: orderIds[0],
        });
        return { success: true, response };
      }

      const response = await getEkidenSdkClient().trade.batchCancelOrders({
        request: orderIds.map((order_id) => ({ sub_account_address: subAccountAddress, symbol, order_id })),
      });

      const hasErrors = response.exit_info.some((info: ExitMessage) => "Error" in info);

      if (hasErrors) {
        return { success: false, error: "Some orders failed to cancel" };
      }

      return { success: true };
    } catch (error) {
      const rawMessage = extractApiErrorMessage(error);
      const message = translateOrderError(rawMessage) ?? rawMessage ?? "Failed to cancel orders";
      return { success: false, error: message };
    }
  };

  closePositions = async (params: ClosePositionsParams): Promise<TradeResult> => {
    const { positions } = params;

    try {
      if (positions.length === 1) {
        const pos = positions[0];
        const closeSide: Side = pos.side === "buy" ? "Sell" : "Buy";

        const response = await getEkidenSdkClient().trade.placeOrder({
          sub_account_address: pos.subAccountAddress,
          symbol: pos.symbol,
          side: closeSide,
          order_type: pos.type === "market" ? "Market" : "Limit",
          qty: pos.size.abs().toString(),
          price: pos.type === "market" ? "0" : pos.price.toString(),
          margin_mode: "Cross" as MarginMode,
          time_in_force: "IOC",
          post_only: false,
          reduce_only: true,
          close_on_trigger: false,
        });

        return { success: true, response };
      }

      const response = await getEkidenSdkClient().trade.batchPlaceOrders({
        request: positions.map((pos) => ({
          sub_account_address: pos.subAccountAddress,
          symbol: pos.symbol,
          side: (pos.side === "buy" ? "Sell" : "Buy") as Side,
          order_type: pos.type === "market" ? "Market" : "Limit",
          qty: pos.size.abs().toString(),
          price: pos.type === "market" ? "0" : pos.price.toString(),
          margin_mode: "Cross" as MarginMode,
          time_in_force: "IOC" as TimeInForce,
          post_only: false,
          reduce_only: true,
          close_on_trigger: false,
        })),
      });

      const hasErrors = response.exit_info.some((info: ExitMessage) => "Error" in info);

      if (hasErrors) {
        return { success: false, error: "Some positions failed to close" };
      }

      return { success: true };
    } catch (error) {
      const rawMessage = extractApiErrorMessage(error);
      const message = translateOrderError(rawMessage) ?? rawMessage ?? "Failed to close positions";
      return { success: false, error: message };
    }
  };

  setLeverage = async (params: SetLeverageParams): Promise<SetLeverageResponse> => {
    return getEkidenSdkClient().position.setLeverage({
      sub_account_address: params.subAccountAddress,
      symbol: params.symbol,
      leverage: params.leverage.toString(),
    });
  };

  setTradingStop = async (params: SetTradingStopParams): Promise<TradeResult> => {
    const { subAccountAddress, symbol, tpSlData, side, entryPrice } = params;

    try {
      const tpsl = this.buildTpSl(tpSlData, side, entryPrice);

      const response: SetTradingStopResponse = await getEkidenSdkClient().position.setTradingStop({
        sub_account_address: subAccountAddress,
        symbol,
        tpsl,
      });

      return { success: response.success };
    } catch (error) {
      const rawMessage = extractApiErrorMessage(error);
      const message = translateOrderError(rawMessage) ?? rawMessage ?? "Failed to update TP/SL";
      return { success: false, error: message };
    }
  };

  private mapTimeInForce = (tif: "GTC" | "IOC" | "FOK" | "PostOnly" | "GTD", gtdExpireAtMs?: number): TimeInForce => {
    switch (tif) {
      case "IOC":
        return "IOC";
      case "FOK":
        return "FOK";
      case "GTD":
        if (gtdExpireAtMs == null || !Number.isFinite(gtdExpireAtMs)) {
          return "GTC";
        }
        return { GTD: Math.trunc(gtdExpireAtMs) };
      default:
        return "GTC";
    }
  };

  private buildTpSl = (tpSlData: TpSlData | undefined, side: "buy" | "sell", price: BN): TpSl | undefined => {
    if (!tpSlData) return undefined;

    const { takeProfitPrice, stopLossPrice } = tpSlData;

    const hasTp = takeProfitPrice.gt(0) && (side === "buy" ? takeProfitPrice.gt(price) : takeProfitPrice.lt(price));
    const hasSl = stopLossPrice.gt(0) && (side === "buy" ? stopLossPrice.lt(price) : stopLossPrice.gt(price));

    if (!hasTp && !hasSl) return undefined;

    const triggerBy: TriggerBy = "LastPrice";

    return {
      mode: "Full" as TpSlMode,
      take_profit: hasTp
        ? {
            market: {
              trigger_price: takeProfitPrice.toString(),
              trigger_by: triggerBy,
            },
          }
        : undefined,
      stop_loss: hasSl
        ? {
            market: {
              trigger_price: stopLossPrice.toString(),
              trigger_by: triggerBy,
            },
          }
        : undefined,
    };
  };
}

export const tradeService = new TradeService();
